import {Form, Formik} from 'formik'
import {FormField, FormRow} from 'layout/form/form'
import {useRouter, useSearchParams} from 'next/navigation'
import React, {useEffect, useState} from 'react'

import * as Sentry from '@sentry/nextjs'
import {Block} from 'blocks/block'
import {RequestForm} from 'blocks/request/request'
import {Theme} from 'layout/theme'
import {DPContactformBlock} from 'types/DPTypes'
import fetch from 'unfetch'
import {useTranslation} from 'util/i18'
import {scrollToHash} from 'util/link'
import {useLinks} from 'util/links'
import {slugify} from 'util/slugify'
import {fromModule} from 'util/styler/Styler'
import {CONTACT_URL} from 'util/urls'
import {useIDFromAnchorBlock} from 'util/useAnchor'
import css from './contactform.module.scss'

const styles = fromModule(css)

type FormData = {
	identification?: string
	topic?: string
	firstname?: string
	lastname?: string
	email?: string
	phone?: string
	question?: string
}

export const Contactform: React.FC<DPContactformBlock> = (data) => {
	const {
		_type,
		company_fields,
		title,
		enable_bar,
		topics,
		identifications,
		no_phone,
		recipients
	} = data
	const router = useRouter()
	const searchParams = useSearchParams()
	const contactblock = searchParams.get('contactblock') || undefined
	const contactperson = searchParams.get('contactperson') || undefined
	const identification = searchParams.get('identification') || undefined
	const origin_url = searchParams.get('origin_url') || undefined
	const subject = searchParams.get('subject') || undefined
	const topic = searchParams.get('topic') || undefined
	const {contactformsubmit} = useLinks()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [unknownError, setUnknownError] = useState(false)
	const [recaptchaError, setRecaptchaError] = useState(false)
	const id = useIDFromAnchorBlock(data)
	const getRecaptchaToken = async () => {
		try {
			const recaptchaToken = await executeRecaptcha()
			if (!recaptchaToken) {
				setRecaptchaError(true)
				return setIsSubmitting(false)
			}
			return recaptchaToken
		} catch (e) {
			setRecaptchaError(true)
			return setIsSubmitting(false)
		}
	}

	useEffect(() => {
		if (
			contactblock ||
			contactperson ||
			origin_url ||
			identification ||
			topic
		) {
			scrollToHash(id)
		}
	})

	return (
		<Block type={_type} anchorID={id} className={styles.contactform()}>
			<Formik<FormData>
				initialValues={{
					identification,
					topic
				}}
				onSubmit={async (values, {setErrors, resetForm}) => {
					setUnknownError(false)
					setRecaptchaError(false)
					if (isSubmitting) return null
					setIsSubmitting(true)
					const token = await getRecaptchaToken()
					if (!token) return null
					return fetch(CONTACT_URL(), {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							...values,
							contactblock,
							origin_url,
							contactperson,
							subject,
							recipients: (recipients || []).map((r) => r.id),
							token
						})
					})
						.then((response) => {
							if (response.ok) {
								resetForm()
								router.push(contactformsubmit.url)
							} else {
								return response.json().then((errors) => {
									if ('recaptcha' in errors) {
										setRecaptchaError(true)
										Sentry.captureException(errors.recaptcha)
									}
									setErrors(errors)
								})
							}
						})
						.catch((error) => {
							console.error(error)
							setUnknownError(true)
						})
						.finally(() => {
							setIsSubmitting(false)
						})
				}}
			>
				<Form>
					{enable_bar && (
						<div className={styles.contactform.request()}>
							<Theme.Container>
								<h3 className={styles.contactform.request.title()}>{title}</h3>
								<RequestForm
									identifications={identifications}
									topics={topics}
								/>
							</Theme.Container>
						</div>
					)}
					<div className={styles.contactform.form()}>
						<Theme.Container>
							{!enable_bar && (
								<Theme.H2
									id={slugify(title)}
									className={styles.form.title()}
									mod="blueline"
								>
									{title}
								</Theme.H2>
							)}
							<ContactformForm
								title={title}
								company_fields={company_fields}
								unknownError={unknownError}
								recaptchaError={recaptchaError}
								hidePhone={no_phone}
							/>
						</Theme.Container>
					</div>
				</Form>
			</Formik>
		</Block>
	)
}

const ContactformForm: React.FC<{
	title?: string
	company_fields: boolean
	unknownError?: boolean
	recaptchaError?: boolean
	hidePhone?: boolean
}> = (data) => {
	const {company_fields, unknownError, recaptchaError, hidePhone} = data
	const searchParams = useSearchParams()
	const subject = searchParams.get('subject')
	const t = useTranslation()

	return (
		<div className={styles.form()}>
			<div className={styles.form.content()}>
				<FormRow>
					<FormField
						mod={{50: true}}
						name="firstname"
						required
						label={t.form.firstname}
					/>
					<FormField
						mod={{50: true}}
						name="lastname"
						required
						label={t.form.lastname}
					/>
				</FormRow>
				<FormField name="email" type="email" required label={t.form.email} />
				{!hidePhone && (
					<FormField
						name="phone"
						type="tel"
						required={process.env.NEXT_PUBLIC_PROJECT_KEY !== 'iclink'}
						label={t.form.phone}
					/>
				)}
				{company_fields && (
					<>
						<br />
						<FormField name="company" required label={t.form.company} />
						<FormField name="address" label={t.form.address} />
						<FormRow>
							<FormField
								mod={{50: true}}
								name="zipcode"
								label={t.form.zipcode}
							/>
							<FormField mod={{50: true}} name="city" label={t.form.city} />
						</FormRow>
						<br />
					</>
				)}
				{subject && (
					<FormField
						name="subject"
						fixedValue={subject as string}
						readOnly={true}
						label={t.form.subject}
					/>
				)}
				<FormField
					as="textarea"
					component="textarea"
					name="message"
					required
					label={t.form.message}
				/>
				{/* This field is for identifying bots and should be left empty. */}
				<FormField name="mobile" className={styles.form.mobile()} />
				<div className={styles.form.button()}>
					<Theme.Button as="button" type="submit" mod="rounded">
						<Theme.ButtonText>{t.form.submit}</Theme.ButtonText>
						<Theme.ButtonIcon />
					</Theme.Button>
				</div>
			</div>
			{unknownError && (
				<div className={styles.form.error()}>
					<p className={styles.form.error.text()}>
						Error: Something went wrong. Please try again later.
					</p>
				</div>
			)}
			{recaptchaError && (
				<div className={styles.form.error()}>
					<p className={styles.form.error.text()}>
						Error: Something went wrong with recaptcha validation. Please try
						again later.
					</p>
				</div>
			)}
		</div>
	)
}

declare global {
	const grecaptcha: {
		enterprise: {
			ready: (cb: () => void) => void
			execute: (siteKey: string, options: {action: string}) => Promise<string>
		}
	}
}

async function executeRecaptcha(): Promise<string | null> {
	if (typeof grecaptcha === 'undefined') {
		return null
	}

	return new Promise((resolve, reject) => {
		grecaptcha.enterprise.ready(async () => {
			try {
				const token = await grecaptcha.enterprise.execute(
					process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!,
					{action: 'contact_form'}
				)
				resolve(token)
			} catch (e) {
				console.error('Recaptcha error', e)
				reject(e)
			}
		})
	})
}
